import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { alertSx, helperTextSx, otpInputSx, primaryButtonSx } from './authFormStyles';

interface Props {
  onSubmit: (code: string) => void;
  loading: boolean;
  error: string | null;
  codeDeliveryDestination?: string | null;
}

const CODE_LENGTH = 6;

export const ConfirmEmailMfaForm: React.FC<Props> = ({
  onSubmit,
  loading,
  error,
  codeDeliveryDestination,
}) => {
  const [code, setCode] = useState('');

  const canSubmit = code.length === CODE_LENGTH && !loading;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(code);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      <Box component="div" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Box
          component="div"
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(199, 20, 68, 0.18) 0%, rgba(199, 20, 68, 0.08) 100%)',
            border: '1px solid rgba(199, 20, 68, 0.32)',
          }}
        >
          <MarkEmailReadOutlinedIcon sx={{ color: '#C71444', fontSize: 28 }} />
        </Box>
        <Typography
          variant="h6"
          sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 500, letterSpacing: '0.02em' }}
        >
          認証コードを入力
        </Typography>
        <Typography variant="body2" sx={{ ...helperTextSx, textAlign: 'center', mt: 0.5 }}>
          {codeDeliveryDestination
            ? `${codeDeliveryDestination} 宛に 6 桁の認証コードを送信しました`
            : 'メールに記載された 6 桁の認証コードを入力してください'}
        </Typography>
      </Box>

      <TextField
        label="認証コード"
        value={code}
        onChange={(e) =>
          setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
        }
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          maxLength: CODE_LENGTH,
          'aria-label': '認証コード',
        }}
        autoFocus
        fullWidth
        sx={otpInputSx}
      />
      {error && (
        <Alert severity="error" sx={alertSx}>
          {error}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        disabled={!canSubmit}
        startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
        sx={primaryButtonSx}
        fullWidth
      >
        確認
      </Button>
    </Box>
  );
};
